import React, { useEffect, useState } from 'react';
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField,
} from '@mui/material';

export const EVENT_OPTIONS = [
  { value: 'new_whatsapp_lead', label: 'A new WhatsApp lead arrives', app: 'WhatsApp' },
  { value: 'new_order', label: 'A new store order arrives', app: 'your store' },
  { value: 'low_stock', label: 'A product runs low on stock', app: 'your store' },
];

const EMPTY = {
  title: '', event: 'new_whatsapp_lead', threshold: 5, prompt: '', conversation_id: '', cooldown_minutes: 30,
};

/** Create or edit a trigger: when something happens in a connected app, a message is sent to one chat. `trigger` null means create. */
export default function TriggerDialog({
  open, trigger, chats, saving, error, onClose, onSave,
}) {
  const [form, setForm] = useState(EMPTY);
  const [localError, setLocalError] = useState('');

  useEffect(() => {
    if (!open) return;
    setLocalError('');
    if (trigger) {
      setForm({
        title: trigger.title,
        event: trigger.event,
        threshold: trigger.threshold ?? 5,
        prompt: trigger.prompt,
        conversation_id: trigger.conversation.id,
        cooldown_minutes: trigger.cooldown_minutes,
      });
    } else {
      const defaultChat = chats.find((c) => c.type === 'group') || chats[0];
      setForm({ ...EMPTY, conversation_id: defaultChat?.id || '' });
    }
  }, [open, trigger, chats]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    if (form.title.trim().length < 2) { setLocalError('Give the trigger a name.'); return; }
    if (form.prompt.trim().length < 3) { setLocalError('Write what the team should do.'); return; }
    if (!form.conversation_id) { setLocalError('Choose a chat.'); return; }
    const wait = Number(form.cooldown_minutes);
    if (!Number.isInteger(wait) || wait < 5 || wait > 1440) { setLocalError('Wait time must be between 5 and 1440 minutes.'); return; }
    const body = {
      title: form.title.trim(),
      event: form.event,
      prompt: form.prompt.trim(),
      conversation_id: form.conversation_id,
      cooldown_minutes: wait,
    };
    if (form.event === 'low_stock') {
      const level = Number(form.threshold);
      if (!Number.isInteger(level) || level < 0 || level > 1000) { setLocalError('Stock level must be a whole number from 0 to 1000.'); return; }
      body.threshold = level;
    }
    setLocalError('');
    onSave(body);
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth component="form" onSubmit={submit}>
      <DialogTitle>{trigger ? 'Edit trigger' : 'New trigger'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {(localError || error) && <Alert severity="error">{localError || error}</Alert>}
          <TextField label="Name" value={form.title} onChange={set('title')} fullWidth autoFocus placeholder="New lead alert" inputProps={{ maxLength: 120 }} />
          <TextField select label="When" value={form.event} onChange={set('event')} fullWidth helperText="Checked every 10 minutes. Nothing is sent to customers.">
            {EVENT_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </TextField>
          {form.event === 'low_stock' && (
            <TextField
              label="Low means at or below"
              type="number"
              value={form.threshold}
              onChange={set('threshold')}
              fullWidth
              inputProps={{ min: 0, max: 1000 }}
              helperText="A product is reported when it falls to this many units or fewer."
            />
          )}
          <TextField
            label="What should the team do?"
            value={form.prompt}
            onChange={set('prompt')}
            multiline
            minRows={3}
            fullWidth
            placeholder="Tell me about each new lead and suggest a short follow-up."
            helperText="Sent to the chat below as if you wrote it, followed by what was found (names and numbers only)."
            inputProps={{ maxLength: 1000 }}
          />
          <TextField select label="Send it to" value={form.conversation_id} onChange={set('conversation_id')} fullWidth>
            {chats.map((c) => <MenuItem key={c.id} value={c.id}>{c.type === 'group' ? `${c.title} (whole team)` : c.title}</MenuItem>)}
          </TextField>
          <TextField
            label="Wait at least (minutes)"
            type="number"
            value={form.cooldown_minutes}
            onChange={set('cooldown_minutes')}
            fullWidth
            inputProps={{ min: 5, max: 1440 }}
            helperText="Between messages, so a busy hour does not spend your AI key. New items found meanwhile are included next time."
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button type="submit" variant="contained" disabled={saving}>{trigger ? 'Save' : 'Create trigger'}</Button>
      </DialogActions>
    </Dialog>
  );
}
